import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";
import { ZentaraLogo, ZentaraMark } from "@/components/Logo";
import { Loader2 } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1705147219565-fe9f6f369d03?w=1920&q=80";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 bg-background lg:grid-cols-2">
      {/* Left visual */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Industrial facility" className="h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0e0e0e] via-[#0e0e0e]/85 to-[#0e0e0e]/45" />
        <div className="absolute inset-0 grid-bg opacity-70" />
        <div className="absolute bottom-0 left-0 p-12">
          <div className="mb-6 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#c5a24a]">
            Reality Capture · Digital Twins · Engineering Documentation
          </div>
          <h1 className="font-heading text-5xl font-bold leading-[1.05] tracking-tight text-[#f7f3ec]">
            Zentara
            <span className="block text-gold-gradient">Redline</span>
          </h1>
          <p className="mt-5 max-w-sm text-base italic leading-relaxed text-neutral-300">
            As-built services that match what's actually in the field.
          </p>
          <p className="mt-2 max-w-sm text-sm leading-relaxed text-neutral-500">
            Track redline-to-drawing progress by area, week over week — and share a live
            status report with your clients.
          </p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center bg-background px-6 py-12 grid-bg">
        <div className="w-full max-w-sm">
          <div className="mb-10 lg:hidden">
            <ZentaraLogo markSize={44} />
          </div>
          <div className="mb-8 hidden lg:block">
            <ZentaraMark size={52} />
          </div>

          <h2 className="font-heading text-3xl font-bold tracking-tight text-[#f7f3ec]">Sign in</h2>
          <p className="mt-1 text-sm text-neutral-500">Access the Zentara Redline tracker.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-neutral-400">
                Email
              </label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-white/15 bg-[#141414] px-3 py-2.5 text-sm text-[#f7f3ec] outline-none transition-colors placeholder:text-neutral-600 focus:border-[#c5a24a] focus:ring-1 focus:ring-[#c5a24a]"
                placeholder="you@zentaraeng.com"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-neutral-400">
                Password
              </label>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-white/15 bg-[#141414] px-3 py-2.5 text-sm text-[#f7f3ec] outline-none transition-colors placeholder:text-neutral-600 focus:border-[#c5a24a] focus:ring-1 focus:ring-[#c5a24a]"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="rounded-lg border border-red-900/50 bg-red-950/40 px-3 py-2 text-sm text-red-300"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="gold-gradient flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-[#121212] transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign in
            </button>
          </form>
          <p className="mt-8 text-xs italic text-neutral-600">
            Excellence grounded in strength and integrity.
          </p>
        </div>
      </div>
    </div>
  );
}
